/** Parents {@link DataTable} for the management page. */

import * as React from 'react';
import { User as UserIcon } from 'lucide-react';
import { DataTable } from '@/components/common/data-table';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { Button } from '@/components/forms/Button';
import { ParentRow } from './ParentRow';
import { ParentCard } from './ParentCard';
import { ParentFiltersContent } from './ParentFilters';
import { ParentDetails } from './renderParentDetails';
import { ArchiveParentModal } from './ArchiveParentModal';
import { ReactivateParentModal } from './ReactivateParentModal';
import { useListParentsRemoteScoped, useGetParentRemoteFullState } from '@/hooks/useUsers';
import { useAuthStore } from '@/stores/authStore';
import { useDataTable } from '@/hooks/useDataTable';
import { useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { cn } from '@/lib/utils';
import { type ParentTabView } from './parent-configs';
import {
  DEFAULT_PARENT_FILTERS,
  ALL_PARENT_FILTER_GROUP_IDS,
  PARENT_FILTER_LABELS,
  type ParentFilters,
  type ParentFilterGroupId,
} from './ParentFilters';
import {
  buildParentRemoteListFilters,
  hasParentRemoteActiveFilters,
  resolveParentExpandedUserId,
  type ParentDataTablePageActions,
} from './parent-table-utils';
import type { ParentRecord, OnboardingFilters } from '@/types/parent.types';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';

/** List filters sent to the parent export endpoint. */
type ParentRemoteExportFilters = Omit<OnboardingFilters, 'companyId' | 'page' | 'pageSize'>;

/** Props for {@link ParentsDataTable}. */
interface Props {
  viewMode?: ViewMode;
  onViewModeChange?: (m: ViewMode) => void;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditParent?: (id: string, record: ParentRecord) => void;
  onInviteChild?: (id: string, record: ParentRecord) => void;
  onViewStudents?: (id: string, record: ParentRecord) => void;
  enableFilters?: boolean;
  activeTab: ParentTabView;
  pageActions?: ParentDataTablePageActions;
  showExport?: boolean;
  /** Same filters as the open list query (without page/pageSize). */
  onExportExcel?: (filters: ParentRemoteExportFilters) => void;
  showCreateButton?: boolean;
  onCreateParent?: () => void;
  isSection?: boolean;
  layoutWidth?: ElementWidth;
  toolbarLeft?: React.ReactNode;
}

/** Parents {@link DataTable} for the management page. */
export const ParentsDataTable: React.FC<Props> = ({
  viewMode: externalViewMode,
  onViewModeChange,
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditParent,
  onInviteChild,
  onViewStudents,
  enableFilters = true,
  activeTab,
  pageActions,
  showExport,
  onExportExcel,
  showCreateButton,
  onCreateParent,
  isSection,
  layoutWidth,
  toolbarLeft,
}) => {
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';

  const dt = useDataTable<ParentFilters, ParentFilterGroupId>({
    tableName: 'parents',
    defaultFilters: DEFAULT_PARENT_FILTERS,
    allFilterGroupIds: ALL_PARENT_FILTER_GROUP_IDS,
    externalViewMode,
    onExternalViewModeChange: onViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
    isSection,
    layoutWidth,
  });

  // Reset to page 1 when tab changes — compare with previous value to skip initial mount
  // (and React 18 StrictMode's double-invoke).
  const prevActiveTabRef = React.useRef(activeTab);
  React.useEffect(() => {
    const prev = prevActiveTabRef.current;
    prevActiveTabRef.current = activeTab;
    if (prev === activeTab) return;
    dt.setCurrentPage(1);
    dt.setFilters((f) => ({ ...f, userStatus: undefined, stepKeys: undefined }));
  }, [activeTab]);

  const apiFilters = React.useMemo(
    () => ({
      ...buildParentRemoteListFilters(dt.filters, activeTab),
      page: dt.currentPage,
      pageSize: dt.itemsPerPage,
    }),
    [dt.filters, activeTab, dt.currentPage, dt.itemsPerPage],
  );

  const exportFilters = React.useMemo(
    (): ParentRemoteExportFilters => buildParentRemoteListFilters(dt.filters, activeTab),
    [dt.filters, activeTab],
  );

  const { data: apiResponse, isLoading } = useListParentsRemoteScoped(apiFilters);

  const data = apiResponse?.data ?? [];
  const totalItems = apiResponse?.total ?? 0;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = Math.min(startIndex + dt.itemsPerPage, totalItems);

  const expandedUserId = resolveParentExpandedUserId(expandedRowId, data);

  const {
    data: expandedFullState,
    isLoading: isLoadingExpandedParent,
    isError: isExpandedParentError,
    error: expandedParentError,
  } = useGetParentRemoteFullState(expandedUserId ?? '', companyId, {
    enabled: !!expandedUserId && !!expandedRowId,
  });
  useUnitGetNotFound({
    isError: isExpandedParentError,
    error: expandedParentError,
    catalogKey: 'parentNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const expandedRecord = expandedFullState?.record ?? null;

  const [archiveUserId, setArchiveUserId] = React.useState<string | null>(null);
  const [reactivateUserId, setReactivateUserId] = React.useState<string | null>(null);

  function handleArchive(userId: string): void {
    setArchiveUserId(userId);
  }

  function handleReactivate(userId: string): void {
    setReactivateUserId(userId);
  }

  function handleCloseArchive(): void {
    setArchiveUserId(null);
  }

  function handleCloseReactivate(): void {
    setReactivateUserId(null);
  }

  const hasActiveFilters = React.useMemo(
    () => hasParentRemoteActiveFilters(dt.filters, activeTab),
    [dt.filters, activeTab],
  );

  const customFilterRender = React.useCallback(
    () => (
      <ParentFiltersContent
        filters={dt.filters}
        visibleFilterIds={dt.visibleFilterIds as ParentFilterGroupId[]}
        onFilterChange={(patch) => {
          dt.setFilters((prev) => ({ ...prev, ...patch }));
          dt.setCurrentPage(1);
        }}
        activeTab={activeTab}
      />
    ),
    [dt.filters, dt.visibleFilterIds, dt.setFilters, dt.setCurrentPage, activeTab],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: customFilterRender,
      },
    ],
    [customFilterRender],
  );

  return (
    <DataTable
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="parents"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={10}
      toolbarActions={
        enableFilters || showExport || showCreateButton ? (
          <>
            {enableFilters && (
              <Button
                preset="toolbar-filter"
                className={cn(dt.showFilters && 'section-action-button-active')}
                showBadge={hasActiveFilters}
                onClick={() => {
                  if (!dt.showFilters) dt.setShowConfigure(false);
                  dt.setShowFilters((prev) => !prev);
                }}
              />
            )}
            {showExport && onExportExcel && (
              <Button
                preset="toolbar-export"
                tooltipText="Exporter la liste des parents en Excel"
                onClick={() => onExportExcel(exportFilters)}
              />
            )}
            {showCreateButton && onCreateParent && (
              <Button
                preset="toolbar-create"
                tooltipText="Créer un parent"
                onClick={() => {
                  onCloseDetails?.();
                  onCreateParent();
                }}
              />
            )}
          </>
        ) : undefined
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={() => {
              dt.setFilters(DEFAULT_PARENT_FILTERS);
              dt.setCurrentPage(1);
            }}
            resultCount={totalItems}
            resultLabel="parent(s)"
            onToggleConfigure={
              dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
            }
            showConfigure={dt.showConfigure}
            configureContent={
              <div className="flex flex-wrap gap-3">
                {ALL_PARENT_FILTER_GROUP_IDS.filter((id) => {
                  if (id === 'filter-registration-step' && activeTab !== 'pre_registered')
                    return false;
                  if (id === 'filter-children-count' && activeTab !== 'parents') return false;
                  return dt.allowedFilterIds.includes(id);
                }).map((id) => {
                  const isOn = dt.filterVisibility[id] ?? true;
                  return (
                    <Button
                      key={id}
                      variant="custom"
                      size="sm"
                      onClick={() => dt.handleToggleFilterVisibility(id)}
                      disabled={dt.isSavingFilterVisibility}
                      className={cn(
                        dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                        isOn ? 'filter-option-primary--active' : 'filter-option-primary',
                      )}
                    >
                      {PARENT_FILTER_LABELS[id]}
                    </Button>
                  );
                })}
              </div>
            }
          />
        ) : undefined
      }
      renderTableHead={() => (
        <tr>
          <th>Nom</th>
          <th>Contact</th>
          <th>Enfants</th>
          <th>Progression</th>
          <th>Étapes</th>
          <th>Inscription</th>
          <th>Statut</th>
          <th className="w-20 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(record) => (
        <ParentRow
          record={record}
          childCount={(record as any)._childrenCount ?? 0}
          childList={(record as any)._children ?? []}
          onView={onToggleRow}
          onEdit={onEditParent}
          onInviteChild={onInviteChild}
          onViewStudents={onViewStudents}
          pageActions={pageActions}
          activeTab={activeTab}
          onArchive={handleArchive}
          onReactivate={handleReactivate}
        />
      )}
      renderCard={(record) => (
        <ParentCard
          record={record}
          childCount={(record as any)._childrenCount ?? 0}
          childList={(record as any)._children ?? []}
          onView={onToggleRow}
          onEdit={onEditParent}
          onInviteChild={onInviteChild}
          onViewStudents={onViewStudents}
          pageActions={pageActions}
          activeTab={activeTab}
          onArchive={handleArchive}
          onReactivate={handleReactivate}
        />
      )}
      expandedRowId={dt.expandedRowId}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      expandedItem={expandedRecord}
      isExpandedDetailLoading={isLoadingExpandedParent}
      renderExpandedRow={(record) => (
        <ParentDetails record={record} fullState={expandedFullState} />
      )}
      renderDetailsContent={(record) => (
        <ParentDetails record={record} fullState={expandedFullState} />
      )}
      onCloseDetails={onCloseDetails}
      emptyIcon={UserIcon}
      emptyText="Aucun parent trouvé"
      isSection={isSection}
      modals={
        <>
          <ArchiveParentModal
            open={!!archiveUserId}
            userId={archiveUserId ?? ''}
            companyId={companyId}
            onClose={handleCloseArchive}
          />
          <ReactivateParentModal
            open={!!reactivateUserId}
            userId={reactivateUserId ?? ''}
            companyId={companyId}
            onClose={handleCloseReactivate}
          />
        </>
      }
      title="Parents"
      sidebarMenuId="menu-parents"
      toolbarLeft={toolbarLeft}
    />
  );
};
